import React, {useEffect, useRef} from 'react';

import {assert} from 'Asserts';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {ProcessFlowchartAdapter} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {
  ConfiguratorResultType,
  useProcessFlowchartEditorConfigurator,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {ProcessFlowchartConfigEditorQuery} from './__generated/ProcessFlowchartConfigEditorQuery';
import {ProcessFlowchartConfigEditorQueryVariables} from './__generated/ProcessFlowchartConfigEditorQuery';
import {isFlowchartConfigWithValidSchemasAndTaskDefinition} from './processFlowchartConfigEditorConstants';
import ProcessFlowchartConfigEditorController from './ProcessFlowchartConfigEditorController';
import {HeaderConfigAdditionalProps} from './ProcessFlowchartConfigEditorHeaderTypes';
import {FlowchartConfigAutosaveManager} from './redux/FlowchartConfigAutosaveManager';
import {PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY} from './useProcessFlowchartConfigQuery';

function NonConfigurableSchema(): JSX.Element {
  return <div>This flowchart is not user-configurable.</div>;
}

function getFlowchartAutosaveFriendlyName(flowchartExecutionType: ProcessExecutionType): string {
  const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(flowchartExecutionType);
  assert(adapter instanceof ProcessFlowchartAdapter, 'Expected adapter to be a ProcessFlowchartAdapter');
  return (adapter.constructor as typeof ProcessFlowchartAdapter).AUTOSAVE_FRIENDLY_NAME;
}

/**
 * Props for the ProcessFlowchartConfigEditor component.
 */
export interface ProcessFlowchartConfigEditorProps {
  flowchartTypeName: string;
  flowchartConfigId: string;
  flowchartConfigVersionId?: string;
  locked: boolean;
  ownerToRegistryMap?: Map<string, string>;
  /**
   * Optional message explaining why the flowchart is locked.
   * Displayed next to the lock icon in the header.
   */
  lockErrorMessage?: string;
  /**
   * Optional metadata and callbacks for dynamically configuring the header.
   */
  headerConfigAdditionalProps?: HeaderConfigAdditionalProps;
  /**
   * Optional info messages to display as banners in the flowchart editor.
   */
  infoBannerMessages?: Array<string>;
}

/**
 * Process flowchart config editor that queries the flowchart config and displays the
 * flowchart editor content.
 *
 * Renders the current flowchart config version unless a specific version is specified.
 */
export default function ProcessFlowchartConfigEditor(props: ProcessFlowchartConfigEditorProps): JSX.Element {
  const hasShownInaccessibleError = useRef(false);
  const configuratorResult = useProcessFlowchartEditorConfigurator(props.flowchartTypeName);

  // Handle error notification in useEffect to prevent multiple notifications on re-renders
  useEffect(() => {
    if (configuratorResult.resultType === ConfiguratorResultType.ERROR) {
      notifications.error(`Unable to load flowchart type configuration for ${props.flowchartTypeName}.`);
    }
  }, [configuratorResult.resultType, props.flowchartTypeName]);

  if (configuratorResult.resultType === ConfiguratorResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  if (configuratorResult.resultType === ConfiguratorResultType.ERROR) {
    return <></>;
  }

  const flowchartEditorConfigurator = configuratorResult.configurator;

  return (
    <BenchlingQuery<ProcessFlowchartConfigEditorQuery, ProcessFlowchartConfigEditorQueryVariables>
      query={PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{
        flowchartConfigId: props.flowchartConfigId,
        flowchartConfigVersionId: props.flowchartConfigVersionId ?? null,
      }}
      fetchPolicy="cache-and-network"
    >
      {(data, {refetch}) => {
        if (isInaccessible(data.processFlowchartConfig)) {
          return (
            <WorkspaceItemInaccessibleGuard
              inaccessibleInfo={data.processFlowchartConfig}
              modelType="processFlowchartConfig"
            />
          );
        }
        const flowchartConfigGQL = data.processFlowchartConfig;

        if (!isFlowchartConfigWithValidSchemasAndTaskDefinition(flowchartConfigGQL)) {
          return <NonConfigurableSchema />;
        }

        const flowchartConfigVersion = flowchartConfigGQL.flowchartConfigVersion;
        if (flowchartConfigVersion != null && isInaccessible(flowchartConfigVersion)) {
          if (!hasShownInaccessibleError.current) {
            hasShownInaccessibleError.current = true;
            notifications.error('You do not have permission to view this version.');
          }
          return null;
        }

        const flowchartExecutionType = flowchartConfigGQL.schema.executionType;
        assert(flowchartExecutionType != null, 'Expected flowchartExecutionType to be non-null');
        const friendlyName = getFlowchartAutosaveFriendlyName(flowchartExecutionType);

        return (
          <FlowchartConfigAutosaveManager
            flowchartConfigId={props.flowchartConfigId}
            friendlyName={friendlyName}
          >
            <ProcessFlowchartConfigEditorController
              flowchartEditorConfigurator={flowchartEditorConfigurator}
              flowchartConfig={flowchartConfigGQL}
              flowchartConfigVersion={flowchartConfigVersion}
              locked={props.locked || !flowchartConfigGQL.permissions.writable}
              lockErrorMessage={props.lockErrorMessage}
              ownerToRegistryMap={props.ownerToRegistryMap}
              headerConfigAdditionalProps={props.headerConfigAdditionalProps}
              infoBannerMessages={props.infoBannerMessages}
              refetchFlowchartConfig={() => {
                refetch();
              }}
            />
          </FlowchartConfigAutosaveManager>
        );
      }}
    </BenchlingQuery>
  );
}
